import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, Users, FileText, Target, Calculator, Percent, ChevronDown } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { toast } from "sonner";
import { InfoTip, RATE_FORMULAS } from "@/components/InfoTip";
import { MarketingSources } from "@/components/MarketingSources";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Generate list of months for selector (last 12 months)
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
};

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return "—";
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

// Stat Card Component
const StatCard = ({ label, value, format = "number", change, changeLabel, icon: Icon, color = "zinc" }) => {
  const colorClasses = {
    zinc: "bg-zinc-100 text-zinc-600",
    green: "bg-green-100 text-green-600",
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
    rose: "bg-rose-100 text-rose-600",
    emerald: "bg-emerald-100 text-emerald-600",
    cyan: "bg-cyan-100 text-cyan-600",
    teal: "bg-teal-100 text-teal-600"
  };

  const formatValue = () => {
    if (value === null || value === undefined) return "—";
    switch (format) {
      case "currency":
        return formatCurrency(value);
      case "percent":
        return `${value.toFixed(1)}%`;
      case "ratio":
        return `${value.toFixed(2)}x`;
      default:
        return value.toLocaleString();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {change !== null && change !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {formatPercent(change)}
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-zinc-900 mb-1">{formatValue()}</div>
      <div className="text-sm text-zinc-500">{label}</div>
      {changeLabel && (
        <div className="text-xs text-zinc-400 mt-1">{changeLabel}</div>
      )}
    </div>
  );
};

export default function MarketingPerformance() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const monthOptions = getMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [stats, setStats] = useState(null);
  const [adSpendInput, setAdSpendInput] = useState("");
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [sourceCount, setSourceCount] = useState(null);
  const refreshStats = useCallback(() => fetchStats(true), [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated, selectedMonth]);

  const fetchStats = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/marketing/stats?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setAdSpendInput(response.data.current.ad_spend > 0 ? response.data.current.ad_spend.toString() : "");
    } catch (error) {
      console.error("Error fetching marketing stats:", error);
      toast.error("Failed to load marketing stats");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdSpend = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const adSpend = parseFloat(adSpendInput) || 0;
      
      await axios.post(`${API}/marketing/ad-spend`, {
        month: selectedMonth,
        ad_spend: adSpend
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Ad spend saved!");
      fetchStats(); // Refresh stats to recalculate metrics
    } catch (error) {
      console.error("Error saving ad spend:", error);
      toast.error("Failed to save ad spend");
    } finally {
      setSaving(false);
    }
  };

  const selectedMonthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto pb-24 p-4">
        {/* Back Button and Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
            data-testid="back-to-settings"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-inter">Back to Settings</span>
          </button>
          
          <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase" data-testid="page-title">
            Marketing Performance
          </h1>
          <p className="font-inter text-zinc-600 mt-1">
            Track your monthly marketing ROI and advertising effectiveness
          </p>
        </div>

        {/* Month Selector */}
        <div className="mb-6 relative">
          <label className="block text-sm font-medium text-zinc-700 mb-2">Select Month</label>
          <button
            onClick={() => setShowMonthDropdown(!showMonthDropdown)}
            className="w-full bg-white border border-zinc-300 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-zinc-400 transition-colors"
            data-testid="month-selector"
          >
            <span className="font-medium text-zinc-900">{selectedMonthLabel}</span>
            <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showMonthDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showMonthDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-auto">
              {monthOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedMonth(option.value);
                    setShowMonthDropdown(false);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-zinc-50 transition-colors ${
                    selectedMonth === option.value ? 'bg-green-50 text-green-700 font-medium' : 'text-zinc-700'
                  }`}
                  data-testid={`month-option-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <MarketingSources month={selectedMonth} monthLabel={selectedMonthLabel} onSaved={refreshStats} onCountChange={setSourceCount} />

        {/* Ad Spend Input (used until spend sources are added) */}
        {sourceCount === 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-6 shadow-sm">
          <label className="block text-sm font-medium text-zinc-700 mb-2">
            Total Ad Spend for {selectedMonthLabel}
          </label>
          <p className="text-xs text-zinc-500 mb-3">
            Enter total ad spend for this month across all channels (Google, Facebook, etc.)
          </p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
              <input
                type="number"
                value={adSpendInput}
                onChange={(e) => setAdSpendInput(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                data-testid="ad-spend-input"
              />
            </div>
            <button
              onClick={handleSaveAdSpend}
              disabled={saving}
              className="px-6 py-3 bg-[#207024] text-white font-semibold rounded-lg hover:bg-[#185a1c] disabled:opacity-50 transition-colors"
              data-testid="save-ad-spend"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
        )}

        {/* Stats Grid */}
        {stats && (
          <>
            {/* Primary Metrics */}
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">Core Metrics</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Total Estimates"
                  value={stats.current.total_estimates}
                  icon={FileText}
                  color="blue"
                  change={stats.changes.estimates_change}
                />
                <StatCard
                  label="Closed Deals"
                  value={stats.current.closed_deals}
                  icon={Target}
                  color="green"
                  change={stats.changes.closed_deals_change}
                />
                <StatCard
                  label="Closed Revenue"
                  value={stats.current.total_revenue}
                  format="currency"
                  icon={DollarSign}
                  color="emerald"
                  change={stats.changes.revenue_change}
                />
                <StatCard
                  label="Ad Spend"
                  value={stats.current.ad_spend}
                  format="currency"
                  icon={TrendingUp}
                  color="amber"
                  change={stats.changes.ad_spend_change}
                />
              </div>
            </div>

            {/* Efficiency Metrics */}
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">Efficiency</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label={<>Close Rate (paid) <InfoTip text={RATE_FORMULAS.close_rate_paid} testid="tip-mp-close-paid" /></>}
                  value={stats.current.estimate_to_close_rate}
                  format="percent"
                  icon={Percent}
                  color="purple"
                />
                <StatCard
                  label={<>Quote Book Rate <InfoTip text={RATE_FORMULAS.quote_book_rate} testid="tip-mp-quote-book" /></>}
                  value={stats.current.quote_book_rate}
                  format="percent"
                  icon={Target}
                  color="emerald"
                />
                <StatCard
                  label="Cost Per Estimate"
                  value={stats.current.cost_per_estimate}
                  format="currency"
                  icon={Calculator}
                  color="cyan"
                />
                <StatCard
                  label="Cost Per Close"
                  value={stats.current.cost_per_closed_deal}
                  format="currency"
                  icon={Users}
                  color="teal"
                />
                <StatCard
                  label="Revenue Per Close"
                  value={stats.current.revenue_per_closed_deal}
                  format="currency"
                  icon={DollarSign}
                  color="green"
                />
              </div>
            </div>

            {/* ROI Metrics */}
            <div className="mb-4">
              <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">Return on Investment</h2>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="ROAS"
                  value={stats.current.roas}
                  format="ratio"
                  icon={TrendingUp}
                  color="rose"
                  changeLabel={stats.current.roas !== null ? `$${stats.current.roas?.toFixed(2)} revenue per $1 spent` : null}
                />
                <StatCard
                  label="ROI"
                  value={stats.current.roi}
                  format="percent"
                  icon={Percent}
                  color="emerald"
                  changeLabel={stats.current.roi !== null ? (stats.current.roi > 0 ? "Profitable" : "Not profitable") : null}
                />
              </div>
            </div>

            {/* Additional Metrics */}
            {stats.current.revenue_per_estimate !== null && (
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">Additional Insights</h2>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    label="Revenue Per Estimate"
                    value={stats.current.revenue_per_estimate}
                    format="currency"
                    icon={FileText}
                    color="zinc"
                  />
                </div>
              </div>
            )}

            {/* Month-over-Month Summary */}
            {(stats.previous.total_estimates > 0 || stats.previous.closed_deals > 0) && (
              <div className="mt-6 bg-zinc-100 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-zinc-600 mb-3">vs Previous Month</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Estimates</span>
                    <span className="font-medium">
                      {stats.previous.total_estimates} → {stats.current.total_estimates}
                      {stats.changes.estimates_change !== null && (
                        <span className={`ml-2 ${stats.changes.estimates_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          ({formatPercent(stats.changes.estimates_change)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Closed Deals</span>
                    <span className="font-medium">
                      {stats.previous.closed_deals} → {stats.current.closed_deals}
                      {stats.changes.closed_deals_change !== null && (
                        <span className={`ml-2 ${stats.changes.closed_deals_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          ({formatPercent(stats.changes.closed_deals_change)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Revenue</span>
                    <span className="font-medium">
                      {formatCurrency(stats.previous.total_revenue)} → {formatCurrency(stats.current.total_revenue)}
                      {stats.changes.revenue_change !== null && (
                        <span className={`ml-2 ${stats.changes.revenue_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          ({formatPercent(stats.changes.revenue_change)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Ad Spend</span>
                    <span className="font-medium">
                      {formatCurrency(stats.previous.ad_spend)} → {formatCurrency(stats.current.ad_spend)}
                      {stats.changes.ad_spend_change !== null && (
                        <span className={`ml-2 ${stats.changes.ad_spend_change >= 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          ({formatPercent(stats.changes.ad_spend_change)})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav activeTab="settings" user={user} />
    </div>
  );
}
